import { Check, Copy } from "lucide-react";
import { type CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { type CodeLines, grammarOf, highlightCode, tooLargeToHighlight } from "./highlight.ts";

/**
 * A code artefact (design §4 and §4a): line numbers, the language as a label, copy, and syntax
 * colours from Shiki's tokens. Every token is a `<span>` whose child is its text (a text node);
 * its only style is Shiki's two theme colour variables. The plain text shows first, and stays
 * when the language is unknown, the file is large or highlighting fails.
 */
export function CodeView(props: {
  /** Names the version, for the token memo (versions are immutable). */
  versionKey: string;
  code: string;
  language: string | null;
}) {
  const { versionKey, code, language } = props;
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<{ key: string; lines: CodeLines } | null>(null);
  const large = tooLargeToHighlight(code);
  useEffect(() => {
    let live = true;
    if (grammarOf(language) && !large) {
      void highlightCode(versionKey, code, language).then((lines) => {
        if (live && lines) {
          setTokens({ key: versionKey, lines });
        }
      });
    }
    return () => {
      live = false;
    };
  }, [versionKey, code, language, large]);
  const lines = tokens?.key === versionKey ? tokens.lines : null;
  const plain = code.split("\n");
  return (
    <figure className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-line bg-surface-sunken">
      <figcaption className="flex h-9 items-center justify-between gap-2 border-b border-line pr-1.5 pl-3.5 text-xs text-fg-muted">
        <span className="truncate font-medium">{language ?? t("artefacts.code.plain")}</span>
        <CopyButton text={code} />
      </figcaption>
      {large ? (
        <p className="border-b border-line px-3.5 py-1.5 text-xs text-fg-muted">
          {t("artefacts.code.large")}
        </p>
      ) : null}
      <pre
        data-highlighted={lines ? "" : undefined}
        className="artefact-code overflow-auto py-3 font-mono text-[13px] leading-[1.6]"
        // Scrollable region: reachable by keyboard to scroll long lines.
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll container must be focusable
        tabIndex={0}
      >
        <code>
          {(lines ?? plain).map((line, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: lines of an immutable version
            <span key={i} className="artefact-line">
              {typeof line === "string"
                ? line
                : line.map((token, j) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: tokens of an immutable line
                    <span key={j} style={token.style as CSSProperties}>
                      {token.content}
                    </span>
                  ))}
              {"\n"}
            </span>
          ))}
        </code>
      </pre>
    </figure>
  );
}

/** Copies the stored text (never the rendered tokens); says "Copied" for a moment. */
export function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => setCopied(false), 1_600);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <Button
      variant="ghost"
      size="xs"
      aria-label={t("artefacts.code.copyLabel")}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(
          () => setCopied(true),
          () => undefined,
        );
      }}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      <span aria-live="polite">
        {copied ? t("artefacts.code.copied") : t("artefacts.code.copy")}
      </span>
    </Button>
  );
}
